// Casper contract utility functions ported from CLI utils
import { blake2b } from "@noble/hashes/blake2";
import { Buffer } from "buffer";
import { BigNumber } from "@ethersproject/bignumber";
import { PublicKey } from "casper-js-sdk";
import { MOTES_IN_CSPR } from "./config";

let stateKey;

export async function getContractNamedKey(rpcClient, contractHash, namedKey) {
  const result = await rpcClient.queryLatestGlobalState("hash-" + contractHash, []);
  const _namedKey = result.storedValue.contract.namedKeys.find((k) => k.name === namedKey);
  if (!_namedKey) {
    throw new Error("state key not found");
  }
  return _namedKey.key.toString();
}

export async function getOdraStateNamedKey(rpcClient, contractHash) {
  return getContractNamedKey(rpcClient, contractHash, "state");
}

/**
 * Fetches the main purse URef for a contract.
 * @param {RpcClient} rpcClient
 * @param {string} contractHash
 * @returns {Promise<string>} Purse URef string
 */
export async function getContractMainPurse(rpcClient, contractHash) {
  return getContractNamedKey(rpcClient, contractHash, "__contract_main_purse");
}

/**
 * Fetches a state item from a contract dictionary.
 * @param {RpcClient} rpcClient
 * @param {string} contract_hash
 * @param {number} index
 * @param {Uint8Array|null} itemBytes
 * @returns {Promise<Uint8Array>}
 */
export async function getStateItem(rpcClient, contract_hash, index, itemBytes) {
  if (!stateKey) {
    stateKey = await getOdraStateNamedKey(rpcClient, contract_hash);
  }
  const bytes = [];
  bytes.push((index >> 24) & 0xff);
  bytes.push((index >> 16) & 0xff);
  bytes.push((index >> 8) & 0xff);
  bytes.push(index & 0xff);
  if (itemBytes) {
    bytes.push(...Array.from(itemBytes));
  }
  const hash = blake2b(new Uint8Array(bytes), { dkLen: 32 });
  const dictionaryItemKey = Buffer.from(hash).toString('hex');
  const result = await rpcClient.getDictionaryItem(null, stateKey, dictionaryItemKey);
  return result.storedValue.clValue.bytes().slice(4);
}

/**
 * Fetches the bonding purse and staked amount for a validator.
 * @param {RpcClient} rpcClient
 * @param {PublicKey} validatorPk
 * @param {CLURef} mainPurse
 * @returns {Promise<[string, BigNumber]>}
 */
export const getStakeInfo = async (rpcClient, validator, delegator) => {
  const bidAddress = "bid-addr-03" +
    validator.accountHash().toHex() +
    (delegator instanceof PublicKey
      ? delegator.accountHash().toHex()
      : delegator.toString().replace("-007", ""));
  const result = await rpcClient.queryLatestGlobalState(bidAddress, []);
  return [
    result.storedValue.bidKind.delegator.bondingPurse.toPrefixedString(),
    BigNumber.from(result.storedValue.bidKind.delegator.stakedAmount.getValue())
  ];
};


/**
 * Converts CSPR to motes.
 * @param {number|string} cspr - Amount in CSPR.
 * @returns {string} Amount in motes (as a string to preserve precision).
 */
export function csprToMotes(cspr) {
  return (Number(cspr) * MOTES_IN_CSPR).toString();
}

/**
 * Converts motes to CSPR.
 * @param {number|string} motes - Amount in motes.
 * @returns {string} Amount in CSPR (up to 9 decimal places).
 */
export function motesToCspr(motes) {
  return (Number(motes) / MOTES_IN_CSPR).toFixed(2);
}